import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Link,
    InputAdornment,
    IconButton,
    Collapse,
    Divider
} from '@mui/material';
import { Visibility, VisibilityOff, ExpandMore, ExpandLess } from '@mui/icons-material';
import { UserLogin } from '../../types/user';
import { useAuth } from '../../contexts/AuthContext';
import GoogleSignIn from '../GoogleSignIn';

// showGoogle=false when the parent already renders the Google button (GoogleSignIn uses a fixed element id).
export const Login: React.FC<{ showGoogle?: boolean }> = ({ showGoogle = true }) => {
    const navigate = useNavigate();
    const { login, googleLogin } = useAuth();
    const [formData, setFormData] = useState<UserLogin>({
        email: '',
        password: ''
    });
    const [error, setError] = useState<string | null>(null);
    const [technicalDetails, setTechnicalDetails] = useState<string | null>(null);
    const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Clear error when user changes form data
    useEffect(() => {
        if (error) {
            setError(null);
            setTechnicalDetails(null);
        }
    }, [formData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClickShowPassword = (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent any form submission
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Don't proceed if already loading
        if (loading) return;
        
        // Validate inputs
        if (!formData.email) {
            setError("Email is required");
            return;
        }
        
        if (!formData.password) {
            setError("Password is required");
            return;
        }
        
        setError(null);
        setTechnicalDetails(null);
        setShowTechnicalDetails(false);
        setLoading(true);

        try {
            await login(formData);
            // Navigate is handled by the login function in AuthContext
        } catch (err: any) {
            logger.error('Login error:', err);
            
            // Display a user-friendly error message
            setError(err.message || 'An error occurred during login. Please try again.');
            
            // Store technical details if available
            if (err.response?.data?.detail) {
                setTechnicalDetails(err.response.data.detail);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (userData: any) => {
        try {
            setLoading(true);
            setError(null);
            
            // Use the googleLogin function from AuthContext for proper authentication flow
            await googleLogin(userData);
            // Navigation is handled by the googleLogin function in AuthContext
        } catch (err: any) {
            logger.error('Google login error:', err);
            setError(err.message || 'Failed to complete Google sign-in. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = (error: string) => {
        setError(error);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default'
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <Typography variant="h4" component="h1" align="center" gutterBottom>
                    Welcome Back
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                        {technicalDetails && (
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                                    startIcon={showTechnicalDetails ? <ExpandLess /> : <ExpandMore />}
                                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                                >
                                    {showTechnicalDetails ? 'Hide technical details' : 'Show technical details'}
                                </Button>
                                <Collapse in={showTechnicalDetails}>
                                    <Box sx={{ 
                                        mt: 1, 
                                        p: 1, 
                                        bgcolor: 'rgba(0,0,0,0.1)', 
                                        borderRadius: 1,
                                        fontSize: '0.8rem',
                                        fontFamily: 'monospace'
                                    }}>
                                        {technicalDetails}
                                    </Box>
                                </Collapse>
                            </Box>
                        )}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        margin="normal"
                        error={!!error && !formData.email}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        margin="normal"
                        error={!!error && !formData.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{ mt: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Sign In'}
                    </Button>
                </form>

                {showGoogle && (
                    <>
                        <Divider sx={{ my: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                OR
                            </Typography>
                        </Divider>

                        <GoogleSignIn
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            disabled={loading}
                        />
                    </>
                )}

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                        component={RouterLink}
                        to="/forgot-password"
                        variant="body2"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        Forgot Password?
                    </Link>
                    <Typography variant="body2">
                        Don't have an account?{' '}
                        <Link component={RouterLink} to="/register">
                            Sign Up
                        </Link>
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}; 